import { orderAdd, orderConfirm } from "@/apis/inventory";
import { RootState } from "@/store";
import { CommonStateEnum } from "@/types/common";
import { ChangeEnum, Inventory, OrderAdd } from "@/types/inventory";
import { Org } from "@/types/org";
import { StoreTypeEnum } from "@/types/product";
import { App } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

export const useOrderAdd = (
  open: ChangeEnum | undefined,
  rootCategoryId: string,
  list: Array<Inventory>,
  handleOk: (info: Array<Inventory>) => void,
  isReturn?: boolean,
) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const curOrg: Org = useSelector(
    (state: RootState) => state.orgReducer.curOrg
  );

  const [loading, setLoading] = useState<boolean>(false);

  const handleAddOrder = (items: OrderAdd) => {
    setLoading(true);
    return new Promise<void>((res, rej) => {
      if (open) {
        orderAdd({
          order: items,
          orgId: curOrg.id,
          rootCategoryId: rootCategoryId,
        })
          .then((data) => {
            return orderConfirm(data.id).then(() => {
              const lists: Array<Inventory> = [];
              data.items.forEach((item) => {
                const info = list.find(
                  (oldItem) => item.productId === oldItem.productId
                );
                if (info) {
                  lists.push({
                    ...info,
                    quantity:
                      open === ChangeEnum.IN || open === ChangeEnum.TEMP_IN
                        ? item.quantity + info.quantity
                        : info.quantity - item.quantity,
                  });
                } else {
                  lists.push({ ...item, preIn: 0, preOut: 0, id: item.id ?? "" });
                }
              });
              data.scans.forEach((item) => {
                item.items?.forEach((codeItem) => {
                  const info = list.find(
                    (oldItem) => codeItem.productId === oldItem.productId
                  );
                  if (info) {
                    lists.push({
                      ...info,
                      quantity:
                        open === ChangeEnum.IN || open === ChangeEnum.TEMP_IN
                          ? info.quantity + 1
                          : info.quantity - 1,
                    });
                  } else {
                    lists.push({
                      preIn: 0,
                      preOut: 0,
                      quantity: 1,
                      id: codeItem.id ?? "",
                      productId: codeItem.productId,
                      product: codeItem.product ?? {
                        id: codeItem.productId,
                        name: "",
                        categoryId: "",
                        code: "",
                        model: "",
                        desc: "",
                        uom: "",
                        category: {
                          id: "",
                          code: "",
                          pId: "",
                          path: "",
                          name: "",
                          desc: "",
                          subs: [],
                          state: CommonStateEnum.ACTIVE,
                          storeType: StoreTypeEnum.CODE,
                          orgs: []
                        },
                        deprecated: null,
                        replacement: null,
                        replacementId: null,
                        process: "",
                        state: CommonStateEnum.ACTIVE,
                        supplier: {
                          code: "",
                          name: "",
                          id: "",
                          docId: ""
                        },
                        preReady: 0,
                        storageRequirements: []
                      },
                    });
                  }
                });
              });
              handleOk(lists);
              if (!isReturn) notification.success({ placement: 'top', message: open === ChangeEnum.TEMP_IN ? t('warehouse.inOK') : t(('warehouse.outOK')) })
            });
          })
          .catch((err) => {
            if (isReturn) return notification.error({ placement: 'top', message: t('warehouse.returnStockFail') })
            const errors: { [key in number]: string } = {
              70802: t("warehouse.outboundNotEnough"),
              70704: t("warehouse.codeRepeat")
            };
            notification.error({
              placement: 'top',
              message:
                open === ChangeEnum.IN || open === ChangeEnum.TEMP_IN
                  ? t("warehouse.inboundError")
                  : t("warehouse.outboundError"),
              description: errors[err] ?? errors[err.code] ?? t("common.serverError"),
            });
            if (typeof (err) !== 'number') {
              rej(err)
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };

  const handleAddDraft = (order: OrderAdd) => {
    setLoading(true);
    return new Promise<void>((res, rej) => {
      if (open) {
        orderAdd({
          order: order,
          orgId: curOrg.id,
          rootCategoryId: rootCategoryId,
        })
          .then(() => {
            handleOk([]);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };

  return { handleAddOrder, handleAddDraft, loading };
};
